// API client for backend communication
const RAW_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = RAW_API_URL.replace(/\/$/, '');
// If API_BASE_URL already ends with /api, only append /v1; else /api/v1
const API_PREFIX = /\/api\/?$/.test(API_BASE_URL) ? '/v1' : '/api/v1';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  is_active: boolean;
  is_superuser: boolean;
}

export interface UserCreate {
  email: string;
  username: string;
  password: string;
  full_name?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}${API_PREFIX}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token exists
    const token = localStorage.getItem('nashezoloto_token');
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        username: credentials.username,
        password: credentials.password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Login failed');
    }

    return await response.json();
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/users/me');
  }

  async createUser(userData: UserCreate): Promise<User> {
    return this.request<User>('/users/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getUsers(skip = 0, limit = 100): Promise<User[]> {
    return this.request<User[]>(`/users/?skip=${skip}&limit=${limit}`);
  }

  async getUserById(userId: string): Promise<User> {
    return this.request<User>(`/users/${userId}`);
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    return this.request<User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck(): Promise<{ status: string; version: string }> {
    return this.request<{ status: string; version: string }>('/health');
  }

  async databaseHealthCheck(): Promise<any> {
    return this.request<any>('/health/database');
  }

  // Chat history (supports env-configured endpoint and fallbacks)
  async getChatHistory(chatId: string, modelId?: string): Promise<any[]> {
    // 1) Custom endpoint via env var (supports :id or {id} placeholder)
    const custom = process.env.REACT_APP_CHAT_HISTORY_ENDPOINT;
    const tryParseArray = (res: any) => {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.items)) return res.items;
      if (res && Array.isArray(res.data)) return res.data;
      return null;
    };

    const appendModel = (url: string) => {
      if (!modelId) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}model_id=${encodeURIComponent(modelId)}`;
    };

    if (custom) {
      let endpoint = custom.replace(':id', encodeURIComponent(chatId)).replace('{id}', encodeURIComponent(chatId));
      endpoint = appendModel(endpoint);
      try {
        const res = await this.request<any>(endpoint);
        const arr = tryParseArray(res);
        if (arr) return arr;
      } catch (e) {
        // fall through to defaults
      }
    }

    // 2) Fallback endpoints
    const endpoints = [
      `/chat/history/${encodeURIComponent(chatId)}`,
      `/chats/${encodeURIComponent(chatId)}/messages`,
      `/chat/${encodeURIComponent(chatId)}/messages`,
      `/messages?branch_id=${encodeURIComponent(chatId)}`,
    ];

    let lastError: unknown = null;
    for (const baseEp of endpoints) {
      try {
        const ep = appendModel(baseEp);
        const res = await this.request<any>(ep);
        const arr = tryParseArray(res);
        if (arr) return arr;
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError || new Error('Chat history endpoint is not available');
  }
}

export const apiClient = new ApiClient();
export default apiClient;
